/* eslint-disable react/no-danger */
/* eslint-disable no-nested-ternary */
import React from 'react';
import { bool, func, instanceOf, object, oneOfType, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, FormattedHTMLMessage, injectIntl, intlShape } from 'react-intl';
import { get } from 'lodash';
import { withRouter } from 'react-router-dom';

import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import config from '../../config';
import { propTypes } from '../../util/types';
import {
  ensureListing,
  ensureUser,
  ensureTransaction,
  ensureBooking,
  ensureStripeCustomer,
  ensurePaymentMethodCard,
} from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import {
  isTransactionInitiateAmountTooLowError,
  isTransactionInitiateListingNotFoundError,
  isTransactionInitiateMissingStripeAccountError,
  isTransactionInitiateBookingTimeNotAvailableError,
  isAccountNotStripeConnected,
  isTransactionChargeDisabledError,
  isTransactionZeroPaymentError,
  transactionInitiateOrderStripeErrors,
  isStripeAccountCapabilityError,
  isUserPendingApprovalError,
} from '../../util/errors';
import { TRANSITION_ENQUIRE_GATED } from '../../util/transaction';
import {
  TransactionUserCard,
  Logo,
  NamedLink,
  NamedRedirect,
  Page,
  StripeCheckoutForm,
  EarlyAccessBar,
  Banner,
} from '../../components';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { handleCardPayment, retrievePaymentIntent } from '../../ducks/stripe.duck';
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';
import { fetchCurrentUser, followSubject } from '../../ducks/user.duck';
import {
  initiateOrder,
  setInitialValues,
  speculateTransaction,
  stripeCustomer,
  confirmPayment,
  sendMessage,
} from './CheckoutPage.duck';
import { storeData } from './CheckoutPageSessionHelpers';
import { useCheckoutPage } from './hooks/useCheckoutPage';
import css from './CheckoutPage.module.css';

import { getFirstName, hasPremiumMembership } from '../../util/user';
import {
  isGuestLiabilityProtectionEnabled,
  getGuestLiabilityProtectionValue,
  isGuestLiabilityProtectionLocked,
  normalizeGlpSelection,
} from '../../util/guestLiabilityProtection';
import BookingDetails from '../../components/TransactionPanel/BookingDetails';
import PackageNotFoundPage from '../PackageNotFoundPage/PackageNotFoundPage';
import { isEarlyAccessAvailable } from '../../util/package';

const { TRIP_INSURANCE_CHECKOUT_ENABLED } = require('../../shared/transaction');

const STORAGE_KEY = 'CheckoutPage';

const stripePromise = loadStripe(config.stripe.publishableKey);

const getGlpSelectionForDisplay = (currentUser, pageData, transaction) => {
  const fromPageData = getGuestLiabilityProtectionValue(
    currentUser,
    pageData?.guestLiabilityProtection
  );
  const fromMetadata = get(transaction, 'attributes.metadata.guestLiabilityProtectionSelection');

  return normalizeGlpSelection(fromPageData ?? fromMetadata);
};

const getTripInsuranceForDisplay = (pageData, transaction) => {
  if (typeof pageData?.tripInsurance === 'boolean') {
    return pageData.tripInsurance;
  }

  const tripInsurance = get(transaction, 'attributes.metadata.tripInsurance');
  return typeof tripInsurance === 'boolean' ? tripInsurance : undefined;
};

export const CheckoutPageComponent = props => {
  const {
    scrollingDisabled,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction: speculatedTransactionMaybe,
    initiateOrderError,
    intl,
    params,
    currentUser,
    retrievePaymentIntentError,
    stripeCustomerFetched,
    fetchUser,
    history,
    followSubjectMutation,
  } = props;

  const {
    pageData,
    dataLoaded,
    submitting,
    usedPromoCode,
    setSubmitting,
    handleSubmit,
    handleApplyPromoCode,
    handleApplyInsurance,
    handleApplyGuestLiabilityProtection,
    handleCompleteLtPlusSubscription,
    handleBeginLtPlusSignup,
    handleEndLtPlusSignup,
  } = useCheckoutPage(props);

  const isLoading = !dataLoaded || speculateTransactionInProgress;
  const currentTransaction = get(history, 'location.state.currentTransaction', null);
  const showInsuranceForm = TRIP_INSURANCE_CHECKOUT_ENABLED;
  const showGuestLiabilityProtectionForm = isGuestLiabilityProtectionEnabled(currentUser);
  const isGuestLiabilityProtectionSelectionLocked = isGuestLiabilityProtectionLocked(currentUser);
  const topbar = (
    <div className={css.topbar}>
      <NamedLink className={css.home} name="LandingPage">
        <Logo
          className={css.logoMobile}
          title={intl.formatMessage({ id: 'CheckoutPage.goToLandingPage' })}
          format="desktop"
        />
        <Logo
          className={css.logoDesktop}
          alt={intl.formatMessage({ id: 'CheckoutPage.goToLandingPage' })}
          format="desktop"
        />
      </NamedLink>
    </div>
  );

  if (!currentUser || isLoading) {
    return (
      <Page>
        {topbar}

        <div className={css.contentContainer}>
          <div className={css.leftLoadingColumn}>
            <div className={css.leftLoader} />
          </div>
          <div className={css.rightLoadingColumn}>
            <div className={css.rightLoader} />
          </div>
        </div>
      </Page>
    );
  }

  const stripeClientSecret = get(
    speculatedTransactionMaybe,
    'attributes.protectedData.stripePaymentIntents.default.stripePaymentIntentClientSecret',
    null
  );

  const stripeOptions = {
    clientSecret: stripeClientSecret,
    appearance: config.stripe.appearance,
  };

  const listingNotFound =
    isTransactionInitiateListingNotFoundError(speculateTransactionError) ||
    isTransactionInitiateListingNotFoundError(initiateOrderError);

  const { listing, bookingDates, transaction } = pageData;
  const existingTransaction = ensureTransaction(transaction);
  const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);
  const currentListing = ensureListing(listing);
  const currentAuthor = ensureUser(currentListing.author);

  const isOwnListing =
    currentUser &&
    currentUser.id &&
    currentAuthor &&
    currentAuthor.id &&
    currentAuthor.id.uuid === currentUser.id.uuid;

  const hasListingAndAuthor = !!(currentListing.id && currentAuthor.id);
  const hasBookingDates = !!(bookingDates && bookingDates.bookingStart && bookingDates.bookingEnd);
  const hasRequiredData = hasListingAndAuthor && hasBookingDates;
  const canShowPage = hasRequiredData && !isOwnListing;
  const shouldRedirect = !isLoading && !canShowPage;

  if (shouldRedirect) {
    // eslint-disable-next-line no-console
    console.error('Missing or invalid data for checkout, redirecting back to listing page.', {
      transaction: speculatedTransaction,
      bookingDates,
      listing,
    });
    return <NamedRedirect name="ListingPage" params={params} />;
  }

  const tx = existingTransaction.booking ? existingTransaction : speculatedTransaction;
  const guestLiabilityProtection = getGlpSelectionForDisplay(currentUser, pageData, tx);
  const tripInsurance = getTripInsuranceForDisplay(pageData, tx);

  const txBooking = ensureBooking(tx.booking);
  const breakdown =
    tx.id && txBooking.id ? (
      <>
        <BookingDetails
          transaction={tx}
          stateData={{
            showPropertyAddress: false,
            showArrivalInstructions: false,
            showListingWaypoints: false,
            showBookingDetails: true,
            showBookingDetailsHeader: true,
          }}
          currentUser={currentUser}
          transactionRole="customer"
          isInquiry={false}
          glpSelection={guestLiabilityProtection}
          isGuestLiabilityProtectionIncluded={isGuestLiabilityProtectionSelectionLocked}
        />
      </>
    ) : null;

  const hasDefaultPaymentMethod = !!(
    stripeCustomerFetched &&
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id
  );

  const listingTitle = currentListing.attributes.title;

  const listingLink = (
    <NamedLink
      name="ListingPage"
      params={{ id: currentListing.id.uuid, slug: createSlug(listingTitle) }}
    >
      <FormattedMessage id="CheckoutPage.errorlistingLinkText" />
    </NamedLink>
  );

  const myTripsLink = (
    <NamedLink name="InboxV2Page" params={{ tab: 'orders' }}>
      <FormattedMessage id="CheckoutPage.errorMyTripsLinkText" />
    </NamedLink>
  );

  const packages = get(pageData.listing, 'attributes.publicData.packages', []);

  const packageInfo = packages.find(item => item.id === params.packageId);

  if (params.packageId && (!packageInfo || !packageInfo.isPublished)) {
    return <PackageNotFoundPage />;
  }

  const userHasPremiumMembership = hasPremiumMembership(currentUser);
  const isEarlyAccess = isEarlyAccessAvailable(packageInfo?.earlyAccessExpiration);

  const isAmountTooLowError = isTransactionInitiateAmountTooLowError(initiateOrderError);
  const isChargeDisabledError = isTransactionChargeDisabledError(initiateOrderError);
  const isBookingTimeNotAvailableError = isTransactionInitiateBookingTimeNotAvailableError(
    initiateOrderError
  );
  const stripeErrors = transactionInitiateOrderStripeErrors(initiateOrderError);

  let initiateOrderErrorMessage = null;
  let listingNotFoundErrorMessage = null;

  if (listingNotFound) {
    listingNotFoundErrorMessage = (
      <p className={css.notFoundError}>
        <FormattedMessage id="CheckoutPage.listingNotFoundError" />
      </p>
    );
  } else if (isAmountTooLowError) {
    initiateOrderErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage id="CheckoutPage.initiateOrderAmountTooLow" />
      </p>
    );
  } else if (isBookingTimeNotAvailableError) {
    initiateOrderErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage id="CheckoutPage.bookingTimeNotAvailableMessage" />
      </p>
    );
  } else if (isChargeDisabledError) {
    initiateOrderErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage id="CheckoutPage.chargeDisabledMessage" />
      </p>
    );
  } else if (stripeErrors && stripeErrors.length > 0) {
    const stripeErrorsAsString = stripeErrors.join(', ');
    initiateOrderErrorMessage = (
      <p className={css.orderError}>
        <FormattedHTMLMessage
          id="CheckoutPage.initiateOrderStripeError"
          values={{ stripeErrors: stripeErrorsAsString }}
        />
      </p>
    );
  } else if (initiateOrderError) {
    initiateOrderErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage
          id="CheckoutPage.initiateOrderError"
          values={{ listingLink, myTripsLink }}
        />
      </p>
    );
  }

  const speculateTransactionErrorMessage = null;
  let speculateErrorMessage = null;
  const authorDisplayName = getFirstName(currentAuthor.attributes.profile.displayName);
  if (isAccountNotStripeConnected(speculateTransactionError)) {
    speculateErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage
          id="CheckoutPage.providerStripeAccountNotConnectedError"
          values={{ landownerName: authorDisplayName }}
        />
      </p>
    );
  } else if (isStripeAccountCapabilityError(speculateTransactionError)) {
    speculateErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage
          id="CheckoutPage.stripeAccountCapabilityError"
          values={{ landownerName: authorDisplayName }}
        />
      </p>
    );
  } else if (isTransactionInitiateMissingStripeAccountError(speculateTransactionError)) {
    speculateErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage
          id="CheckoutPage.providerStripeAccountMissingError"
          values={{ landownerName: authorDisplayName }}
        />
      </p>
    );
  } else if (isTransactionInitiateBookingTimeNotAvailableError(speculateTransactionError)) {
    speculateErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage id="CheckoutPage.bookingTimeNotAvailableMessage" />
      </p>
    );
  } else if (isTransactionZeroPaymentError(speculateTransactionError)) {
    speculateErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage id="CheckoutPage.initiateOrderAmountTooLow" />
      </p>
    );
  } else if (isUserPendingApprovalError(speculateTransactionError)) {
    speculateErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage id="CheckoutPage.pendingApprovalError" />
      </p>
    );
  } else if (speculateTransactionError) {
    speculateErrorMessage = (
      <p className={css.orderError}>
        <FormattedMessage
          id="CheckoutPage.speculateFailedMessage"
          values={{ landownerName: authorDisplayName }}
        />
      </p>
    );
  }

  const currentTransactionId = get(
    currentTransaction,
    'attributes.protectedData.selectedPackage.packageId',
    null
  );
  const showInitialMessageInput = !(
    currentTransaction &&
    currentTransaction?.attributes?.lastTransition === TRANSITION_ENQUIRE_GATED &&
    currentTransactionId === packageInfo.id
  );

  const pageProps = { listingTitle, scrollingDisabled };

  if (isLoading) {
    return (
      <Page {...pageProps} noindex nofollow>
        {topbar}

        <div className={css.contentContainer}>
          <div className={css.leftLoadingColumn}>
            <div className={css.leftLoader} />
          </div>
          <div className={css.rightLoadingColumn}>
            <div className={css.rightLoader} />
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page {...pageProps} noindex nofollow>
      {topbar}

      <EarlyAccessBar
        isEarlyAccess={isEarlyAccess}
        userHasPremiumMembership={userHasPremiumMembership}
        expirationTimestamp={packageInfo?.earlyAccessExpiration}
      />

      <div className={css.contentContainer}>
        <div className={css.mobileUserCard}>
          <TransactionUserCard
            listing={listing}
            packageInfo={packageInfo}
            currentUser={currentUser}
          />
        </div>

        <div className={css.bookListingContainer}>
          <section className={css.paymentContainer}>
            {initiateOrderErrorMessage}
            {listingNotFoundErrorMessage}
            {speculateErrorMessage}
            {retrievePaymentIntentError ? (
              <p className={css.orderError}>
                <FormattedMessage
                  id="CheckoutPage.retrievingStripePaymentIntentFailed"
                  values={{ listingLink }}
                />
              </p>
            ) : null}

            {stripeClientSecret && (
              <Elements stripe={stripePromise} options={stripeOptions}>
                <StripeCheckoutForm
                  submitClassName={css.submitClassName}
                  onSubmit={handleSubmit}
                  onApplyPromoCode={handleApplyPromoCode}
                  onApplyInsurance={handleApplyInsurance}
                  onApplyGuestLiabilityProtection={handleApplyGuestLiabilityProtection}
                  onCompleteLtPlusSubscription={handleCompleteLtPlusSubscription}
                  onBeginLtPlusSignup={handleBeginLtPlusSignup}
                  onEndLtPlusSignup={handleEndLtPlusSignup}
                  guestLiabilityProtection={guestLiabilityProtection}
                  tripInsurance={tripInsurance}
                  isGuestLiabilityProtectionLocked={isGuestLiabilityProtectionSelectionLocked}
                  showInitialMessageInput={showInitialMessageInput}
                  followSubjectMutation={followSubjectMutation}
                  authorDisplayName={getFirstName(currentAuthor.attributes.profile.displayName)}
                  transaction={tx}
                  usedPromoCode={usedPromoCode}
                  clientSecret={stripeClientSecret}
                  defaultPaymentMethod={
                    hasDefaultPaymentMethod ? currentUser.stripeCustomer.defaultPaymentMethod : null
                  }
                  isSubmitting={submitting}
                  setIsSubmitting={setSubmitting}
                  showInsuranceForm={showInsuranceForm}
                  showGuestLiabilityProtectionForm={showGuestLiabilityProtectionForm}
                  shouldShowInsuranceDisclaimer={showInsuranceForm}
                  fetchUser={fetchUser}
                  user={currentUser}
                  listing={listing}
                  bookingBreakdown={
                    <div className={css.mobileUserCard}>
                      {speculateTransactionErrorMessage}
                      {breakdown}
                    </div>
                  }
                />

                <div className={css.bookingBreakdownContainer}>
                  <Banner
                    header={intl.formatMessage({ id: 'PricingDetails.bannerHeader' })}
                    body={intl.formatMessage({ id: 'PricingDetails.bannerText' })}
                  />
                </div>
              </Elements>
            )}
          </section>
        </div>

        <div className={css.bookingBreakdownContainerDesktop}>
          <div className={css.desktopUserCardBreakdown}>
            <TransactionUserCard
              error={speculateTransactionErrorMessage}
              listing={listing}
              breakdown={breakdown}
              packageInfo={packageInfo}
              currentUser={currentUser}
            />
          </div>
        </div>
      </div>
    </Page>
  );
};

CheckoutPageComponent.defaultProps = {
  initiateOrderError: null,
  confirmPaymentError: null,
  listing: null,
  bookingData: {},
  bookingDates: null,
  speculateTransactionError: null,
  speculatedTransaction: null,
  updateSpeculateTransactionError: null,
  updateSpeculatedTransaction: null,
  transaction: null,
  currentUser: null,
  paymentIntent: null,
  handleCardPaymentError: null,
  includeLodging: null,
};

CheckoutPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  bookingData: object,
  bookingDates: shape({
    bookingStart: instanceOf(Date).isRequired,
    bookingEnd: instanceOf(Date).isRequired,
  }),
  fetchStripeCustomer: func.isRequired,
  stripeCustomerFetched: bool.isRequired,
  fetchSpeculatedTransaction: func.isRequired,
  speculateTransactionInProgress: bool.isRequired,
  speculateTransactionError: propTypes.error,
  speculatedTransaction: propTypes.transaction,
  updateSpeculateTransactionInProgress: bool.isRequired,
  updateSpeculateTransactionError: propTypes.error,
  updateSpeculatedTransaction: propTypes.transaction,
  transaction: propTypes.transaction,
  currentUser: propTypes.currentUser,
  params: shape({
    id: string,
    slug: string,
  }).isRequired,
  onConfirmPayment: func.isRequired,
  onInitiateOrder: func.isRequired,
  onHandleCardPayment: func.isRequired,
  onRetrievePaymentIntent: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onSendMessage: func.isRequired,
  initiateOrderError: propTypes.error,
  confirmPaymentError: propTypes.error,
  handleCardPaymentError: oneOfType([propTypes.error, object]),
  paymentIntent: object,
  includeLodging: bool,

  dispatch: func.isRequired,

  intl: intlShape.isRequired,

  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const {
    listing,
    bookingData,
    bookingDates,
    stripeCustomerFetched,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    updateSpeculateTransactionInProgress,
    updateSpeculateTransactionError,
    updateSpeculatedTransaction,
    transaction,
    initiateOrderError,
    confirmPaymentError,
    includeLodging,
  } = state.CheckoutPage;
  const { currentUser } = state.user;

  const { handleCardPaymentError, paymentIntent, retrievePaymentIntentError } = state.stripe;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    stripeCustomerFetched,
    bookingData,
    bookingDates,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    updateSpeculateTransactionInProgress,
    updateSpeculateTransactionError,
    updateSpeculatedTransaction,
    transaction,
    listing,
    initiateOrderError,
    handleCardPaymentError,
    confirmPaymentError,
    paymentIntent,
    retrievePaymentIntentError,
    includeLodging,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  fetchSpeculatedTransaction: (params, isUpdating) =>
    dispatch(speculateTransaction(params, isUpdating)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  fetchUser: () => dispatch(fetchCurrentUser()),
  followSubjectMutation: (userId, subjectId, notifyBy) =>
    dispatch(followSubject(userId, subjectId, notifyBy)),
  onInitiateOrder: (params, transactionId, tx) =>
    dispatch(initiateOrder(params, transactionId, tx)),
  onRetrievePaymentIntent: params => dispatch(retrievePaymentIntent(params)),
  onHandleCardPayment: params => dispatch(handleCardPayment(params)),
  onConfirmPayment: params => dispatch(confirmPayment(params)),
  onSendMessage: params => dispatch(sendMessage(params)),
  onSavePaymentMethod: (stripeCustomer, stripePaymentMethodId) =>
    dispatch(savePaymentMethod(stripeCustomer, stripePaymentMethodId)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const CheckoutPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(CheckoutPageComponent);

CheckoutPage.setInitialValues = (initialValues, saveToSessionStorage = false) => {
  if (saveToSessionStorage) {
    const { listing, bookingData, bookingDates, includeLodging } = initialValues;
    storeData(bookingData, bookingDates, listing, null, STORAGE_KEY, includeLodging);
  }

  return setInitialValues(initialValues);
};

CheckoutPage.displayName = 'CheckoutPage';

export default CheckoutPage;
